import { Delete as DeleteIcon } from '@mui/icons-material'
import { Search as SearchIcon } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useContext, useEffect, useState } from 'react'

import { Modal } from '../../../components/Modal/Modal'
import {
  PatientsContext,
  PatientsContextInterface,
} from '../../../providers/Patients/Patients.provider'
import { Patient } from '../../../providers/Patients/types'
import {
  ResourceFilePatient,
  ResourcesContext,
  ResourcesContextInterface,
} from '../../../providers/Resources/Resources.provider'
import { themeColors } from '../../../theme'
import { dateFormatters } from '../../../utilities/HelperFunctions'
import { ResourceFile } from './Resources.view'

interface AssignResourceFileModalInterface {
  assignModalOpen: boolean
  setAssignModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  fileToAssign: ResourceFile | null
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export const AssignResourceFileModal = ({
  assignModalOpen,
  setAssignModalOpen,
  fileToAssign,
  setLoading,
}: AssignResourceFileModalInterface) => {
  const { allActivePatients, getListOfAllActivePatients } = useContext(
    PatientsContext
  ) as PatientsContextInterface

  const {
    markFileVisibleToPatient,
    unassignFileFromPatients,
    getPatientsAssignedToFile,
  } = useContext(ResourcesContext) as ResourcesContextInterface

  // State management
  const [tabValue, setTabValue] = useState(0)
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [assignToAll, setAssignToAll] = useState<boolean>(false)
  const [assignedPatients, setAssignedPatients] = useState<
    ResourceFilePatient[]
  >([])
  const [isLoadingAssignedPatients, setIsLoadingAssignedPatients] =
    useState(false)
  const [searchTerm, setSearchTerm] = useState<string>('') //for unassign

  useEffect(() => {
    if (assignModalOpen) {
      setAssignedPatients([])
      setTabValue(0)
      setSearchTerm('')
      getListOfAllActivePatients()
      loadAssignedPatients()
    }
  }, [assignModalOpen, fileToAssign])

  // Memoized load function to prevent unnecessary recreations
  const loadAssignedPatients = useCallback(async () => {
    if (!fileToAssign) return
    setIsLoadingAssignedPatients(true)

    try {
      const patients = await getPatientsAssignedToFile(fileToAssign.id)
      setAssignedPatients(patients)
    } catch (error) {
      console.error('Error loading assigned patients:', error)
    } finally {
      setIsLoadingAssignedPatients(false)
    }
  }, [fileToAssign?.id, getPatientsAssignedToFile])

  const getFilteredAssignedPatients = () => {
    if (!searchTerm.trim()) return assignedPatients

    return assignedPatients.filter(
      (patient) =>
        patient.preferredName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        patient.mrn.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }

  const getUnassignedPatients = () => {
    const assignedPatientIds = assignedPatients?.map((p) => p.patientId)
    return (
      allActivePatients?.filter(
        (p) => !assignedPatientIds.includes(p.patientId)
      ) || []
    )
  }

  const filteredAssignedPatients = getFilteredAssignedPatients()
  const unassignedPatients = getUnassignedPatients()

  const handleAssignFile = async () => {
    if (!assignToAll && !selectedPatient) return
    setLoading(true)
    setIsLoadingAssignedPatients(true)
    setTabValue(1)

    try {
      let patientIdsToAssign: number[] = []

      if (assignToAll) {
        // Get unassigned patients only
        const unassignedPatients = getUnassignedPatients()
        patientIdsToAssign = unassignedPatients.map((p) => p.patientId)
      } else if (selectedPatient) {
        patientIdsToAssign = [selectedPatient.patientId]
      }

      if (patientIdsToAssign.length > 0 && fileToAssign) {
        const request = {
          fileId: fileToAssign.id,
          patientIds: patientIdsToAssign,
        }
        await markFileVisibleToPatient(request)
        await loadAssignedPatients() // Refresh the assigned patients list
      }
      setSelectedPatient(null) // Reset form
      setAssignToAll(false)
    } catch (error) {
      console.error('Error assigning file:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAssignToAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAssignToAll(event.target.checked)
    if (event.target.checked) {
      setSelectedPatient(null)
    }
  }

  const handleUnassignSinglePatient = async (patient: ResourceFilePatient) => {
    setLoading(true)
    setIsLoadingAssignedPatients(true)

    try {
      if (fileToAssign) {
        const request = {
          fileId: fileToAssign.id,
          patientIds: [patient.patientId],
        }
        await unassignFileFromPatients(request)
        await loadAssignedPatients()
        setSearchTerm('')
      }
    } catch (error) {
      console.error('Error unassign patient:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUnassignAllPatients = async () => {
    if (assignedPatients.length === 0) return
    setLoading(true)
    setIsLoadingAssignedPatients(true)

    try {
      if (fileToAssign) {
        const request = {
          fileId: fileToAssign.id,
          patientIds: assignedPatients.map((p) => p.patientId),
        }
        await unassignFileFromPatients(request)
        await loadAssignedPatients()
      }
    } catch (error) {
      console.error('Error unassign all patients:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleModalClose = () => {
    setAssignModalOpen(false)
    setSelectedPatient(null)
    setAssignToAll(false)
    setTabValue(0)
  }

  return (
    <Modal
      title={`Manage File Access: ${fileToAssign?.name}`}
      open={assignModalOpen}
      setOpen={setAssignModalOpen}
      ModalContent={
        <Box sx={{ width: '100%', minHeight: '400px' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
          >
            <Tab label='Assign' />
            <Tab label='Unassign' />
          </Tabs>

          {isLoadingAssignedPatients ? (
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 2 }}>
              <CircularProgress size={20} color='error' />
              <Typography color={themeColors.red} p={2} fontSize={15}>
                Loading patient data...
              </Typography>
            </Box>
          ) : tabValue === 0 ? (
            <Typography color={themeColors.red} p={2} fontSize={15}>
              {unassignedPatients?.length
                ? `Note: ${unassignedPatients?.length} patients available.`
                : 'Note: This file has already been assigned to all patients.'}
            </Typography>
          ) : (
            <Typography color={themeColors.red} p={2} fontSize={15}>
              {assignedPatients?.length
                ? `Note: ${assignedPatients?.length} patients assigned.`
                : null}
            </Typography>
          )}

          {/* Assign Tab */}
          {tabValue === 0 && (
            <Box sx={{ p: 2, pt: 0 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={assignToAll}
                    onChange={handleAssignToAllChange}
                    color='primary'
                  />
                }
                label={`Assign to All Available Patients (${unassignedPatients.length})`}
                sx={{ mb: 2 }}
              />

              <Autocomplete
                options={unassignedPatients}
                getOptionLabel={(option: Patient) => option.preferredName}
                value={selectedPatient}
                onChange={(event, newValue: Patient | null) => {
                  if (!assignToAll) {
                    setSelectedPatient(newValue)
                  }
                }}
                disabled={assignToAll || unassignedPatients.length === 0}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label='Search and select patient'
                    variant='outlined'
                    placeholder={
                      unassignedPatients.length === 0
                        ? 'All patients already have access'
                        : 'Type patient name...'
                    }
                    disabled={assignToAll || unassignedPatients.length === 0}
                  />
                )}
                renderOption={(props, option: Patient) => (
                  <Box component='li' {...props}>
                    {option.preferredName}
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      paddingLeft={1}
                    >
                      #{option.mrn}
                    </Typography>
                  </Box>
                )}
                filterOptions={(options, { inputValue }) => {
                  return options.filter((patient: Patient) =>
                    patient.preferredName
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  )
                }}
                noOptionsText='No available patients found'
              />

              <Box
                sx={{
                  mt: 3,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                }}
              >
                <Button onClick={handleModalClose}>Cancel</Button>
                <Button
                  onClick={handleAssignFile}
                  disabled={
                    (!assignToAll && !selectedPatient) ||
                    unassignedPatients.length === 0
                  }
                  variant='contained'
                >
                  {assignToAll
                    ? `Assign to ${unassignedPatients.length} Patients`
                    : 'Assign to Patient'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Unassign Tab */}
          {tabValue === 1 && (
            <Box sx={{ p: 1 }}>
              {assignedPatients?.length === 0 ? (
                <Typography
                  color='text.secondary'
                  sx={{ textAlign: 'center', py: 2 }}
                >
                  No patients currently have access to this file
                </Typography>
              ) : (
                <Box sx={{ height: 350, overflowY: 'scroll' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '80%',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant='h6'>
                      Patients with Access ({assignedPatients.length})
                    </Typography>
                    <Button
                      onClick={handleUnassignAllPatients}
                      color='error'
                      variant='contained'
                      size='small'
                    >
                      Remove All Access
                    </Button>

                    <TextField
                      size='small'
                      placeholder='Search patients...'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ width: 250 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <List style={{ height: 300 }}>
                    {filteredAssignedPatients.map(
                      (patient: ResourceFilePatient, index) => (
                        <Box key={patient.patientId}>
                          <ListItem
                            sx={{
                              '&:hover': { backgroundColor: 'action.hover' },
                            }}
                          >
                            <ListItemText
                              primary={patient.preferredName}
                              secondary={`MRN: ${patient.mrn}`}
                            />
                            <Box>
                              <ListItemText
                                primary={`Assigned By: ${patient.fileAssignedBy}`}
                              />
                              <ListItemText
                                primary={`Assigned On: ${dateFormatters.shortenedCleanDate(
                                  patient.fileAssignedOn
                                )}`}
                              />
                            </Box>
                            <ListItemSecondaryAction>
                              <IconButton
                                edge='end'
                                aria-label='unassign'
                                onClick={() =>
                                  handleUnassignSinglePatient(patient)
                                }
                                color='error'
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < assignedPatients.length - 1 && <Divider />}
                        </Box>
                      )
                    )}
                  </List>
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'fixed', justifyContent: 'flex-end' }}>
                <Button onClick={handleModalClose} variant='contained'>
                  Close
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      }
    />
  )
}
